import type { UserRole } from "@conlit/domain";
import type { User } from "@supabase/supabase-js";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { getSupabaseClient } from "@/lib/supabase/client";

export type PrincipalKind =
  UserRole | "superadmin" | "invited" | "suspended" | "participant" | "anonymous";
interface AuthContextValue {
  principal: PrincipalKind;
  roles: UserRole[];
  user: User | null;
  isLoading: boolean;
  isDevelopmentAdapter: boolean;
  setDevelopmentPrincipal: (principal: PrincipalKind) => void;
  refreshPrincipal: () => Promise<PrincipalKind>;
  signOut: () => Promise<void>;
}
const AuthContext = createContext<AuthContextValue | null>(null);
const storageKey = "conlit.dev-principal";
const allowed = new Set<PrincipalKind>([
  "teacher",
  "researcher",
  "administrator",
  "superadmin",
  "invited",
  "suspended",
  "participant",
  "anonymous",
]);

export function AuthProvider({ children }: { children: ReactNode }) {
  const isDevelopmentAdapter =
    import.meta.env.DEV && import.meta.env.VITE_ENABLE_DEV_AUTH === "true";
  const [developmentPrincipal, setPrincipal] = useState<PrincipalKind>(() => {
    const stored = globalThis.localStorage?.getItem(storageKey) as PrincipalKind | null;
    return stored && allowed.has(stored) ? stored : "teacher";
  });
  const [user, setUser] = useState<User | null>(null);
  const [roles, setRoles] = useState<UserRole[]>([]);
  const [resolvedPrincipal, setResolvedPrincipal] = useState<PrincipalKind>("anonymous");
  const [isLoading, setLoading] = useState(!isDevelopmentAdapter);
  const resolvePrincipal = useCallback(async (nextUser: User | null): Promise<PrincipalKind> => {
    const supabase = getSupabaseClient();
    setUser(nextUser);
    if (!supabase || !nextUser) {
      setRoles([]);
      setResolvedPrincipal("anonymous");
      setLoading(false);
      return "anonymous";
    }
    if (nextUser.is_anonymous) {
      const { count } = await supabase
        .from("participants")
        .select("id", { count: "exact", head: true })
        .eq("auth_user_id", nextUser.id);
      setRoles([]);
      const participantPrincipal = (count ?? 0) > 0 ? "participant" : "anonymous";
      setResolvedPrincipal(participantPrincipal);
      setLoading(false);
      return participantPrincipal;
    }
    const [accountContext, organisationRoles, studyRoles] = await Promise.all([
      supabase.rpc("get_account_context"),
      supabase.from("organisation_memberships").select("role").eq("user_id", nextUser.id),
      supabase.from("study_memberships").select("role").eq("user_id", nextUser.id),
    ]);
    const context = accountContext.data as {
      isPlatformAdministrator?: boolean;
      staffStatus?: "invited" | "active" | "suspended" | null;
    } | null;
    const nextRoles = [
      ...new Set(
        [...(organisationRoles.data ?? []), ...(studyRoles.data ?? [])].map(
          (membership) => membership.role,
        ),
      ),
    ];
    setRoles(nextRoles);
    const nextPrincipal: PrincipalKind = context?.isPlatformAdministrator
      ? "superadmin"
      : context?.staffStatus === "suspended"
        ? "suspended"
        : context?.staffStatus === "invited"
          ? "invited"
          : nextRoles.includes("teacher")
            ? "teacher"
            : nextRoles.includes("researcher")
              ? "researcher"
              : nextRoles.includes("administrator")
                ? "administrator"
                : "anonymous";
    setResolvedPrincipal(nextPrincipal);
    setLoading(false);
    return nextPrincipal;
  }, []);
  useEffect(() => {
    if (isDevelopmentAdapter) return;
    const supabase = getSupabaseClient();
    if (!supabase) {
      setLoading(false);
      return;
    }
    void supabase.auth.getUser().then(({ data }) => resolvePrincipal(data.user));
    const { data } = supabase.auth.onAuthStateChange((_event, session) => {
      void resolvePrincipal(session?.user ?? null);
    });
    return () => data.subscription.unsubscribe();
  }, [isDevelopmentAdapter, resolvePrincipal]);
  const principal = isDevelopmentAdapter ? developmentPrincipal : resolvedPrincipal;
  const value = useMemo<AuthContextValue>(
    () => ({
      principal,
      roles: isDevelopmentAdapter
        ? ([developmentPrincipal].filter((role) =>
            ["teacher", "researcher", "administrator"].includes(role),
          ) as UserRole[])
        : roles,
      user,
      isLoading,
      isDevelopmentAdapter,
      setDevelopmentPrincipal: (next) => {
        if (isDevelopmentAdapter) {
          localStorage.setItem(storageKey, next);
          setPrincipal(next);
        }
      },
      refreshPrincipal: async () => {
        const supabase = getSupabaseClient();
        const { data } = (await supabase?.auth.getUser()) ?? { data: { user: null } };
        return resolvePrincipal(data.user);
      },
      signOut: async () => {
        await getSupabaseClient()?.auth.signOut();
        await resolvePrincipal(null);
      },
    }),
    [
      developmentPrincipal,
      isDevelopmentAdapter,
      isLoading,
      principal,
      resolvePrincipal,
      roles,
      user,
    ],
  );
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// This hook intentionally shares the context module with its provider.
// eslint-disable-next-line react-refresh/only-export-components
export function useAuth(): AuthContextValue {
  const value = useContext(AuthContext);
  if (!value) throw new Error("useAuth must be used within AuthProvider");
  return value;
}
